import { useCallback, useEffect, useState } from "react";
import { getAllSuppliers, getAssets, getCategories, getContracts, unwrapList } from "../../../services";
import { DashboardFilterOption } from "../../../types/dashboard";
import {
  useGetStoredSelectedCategories,
  useGetStoredSelectedClients,
  useGetStoredSelectedContracts,
  useGetStoredSelectedVehicles,
  setSelectedCategories,
  setSelectedClients,
  setSelectedContracts,
  setSelectedVehicles,
} from "@store/volksAssetTree/volksAssetTreeSlice";

export interface IFilter {
  id: string;
  label: string;
}

export interface IFilterOptions {
  categories: IFilter[];
  clients: IFilter[];
  contracts: IFilter[];
  vehicles: IFilter[];
}

export const useAssetTree = () => {
  const selectedCategories = useGetStoredSelectedCategories();
  const selectedClients = useGetStoredSelectedClients();
  const selectedContracts = useGetStoredSelectedContracts();
  const selectedVehicles = useGetStoredSelectedVehicles();

  const [filterOptions, setFilterOptions] = useState<IFilterOptions>({
    categories: [],
    clients: [],
    contracts: [],
    vehicles: [],
  });

  const getVehicleLabel = (vehicle: Record<string, unknown>): string | undefined => {
    return (
      (vehicle.name as string | undefined) ??
      (vehicle.licensePlate as string | undefined) ??
      (vehicle.license_plate as string | undefined) ??
      (vehicle.model as string | undefined) ??
      (vehicle.displayName as string | undefined)
    );
  };

  const toFilterOptions = (
    items: unknown,
    getLabel: (item: Record<string, unknown>) => string | undefined
  ): DashboardFilterOption[] => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.reduce<DashboardFilterOption[]>((acc, rawItem) => {
      const item = rawItem as Record<string, unknown>;
      const label = getLabel(item);
      if (!label) {
        return acc;
      }

      const idCandidate =
        item.id ?? item.value ?? item.uuid ?? item.name ?? item.licensePlate ?? item.license_plate ?? label;

      acc.push({ id: String(idCandidate), label });
      return acc;
    }, []);
  };

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [categories, clients, contracts, vehicles] = await Promise.all([
        getCategories(),
        getAllSuppliers(),
        getContracts(),
        getAssets(),
      ]);

      const clientsUnwrap = unwrapList(clients);
      const contractsUnwrap = unwrapList(contracts);
      const vehiclesUnwrap = unwrapList(vehicles);

      setFilterOptions({
        categories: toFilterOptions(categories, (category) => category.name as string | undefined),
        clients: toFilterOptions(clientsUnwrap, (client) => client.name as string | undefined),
        contracts: contractsUnwrap,
        vehicles: toFilterOptions(vehiclesUnwrap, getVehicleLabel),
      });
    } catch (error) {
      console.error("Error fetching dashboard filter options:", error);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  return {
    selectedCategories,
    selectedClients,
    selectedContracts,
    selectedVehicles,
    filterOptions,
    setSelectedCategories,
    setSelectedClients,
    setSelectedContracts,
    setSelectedVehicles,
  };
};

export default useAssetTree;
